import { type MaybeRefOrGetter, toValue } from "vue";

import i18n from "@/plugins/i18n";
import { RouteRedispatchState, RouteStatus, RouteStopStatus } from "@/constants/trip";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import { useBaseView } from "@/composables/useBaseView";

import type { TripMonitorRoute } from "@/api/trips/dto/trip-monitor";
import type { DisableRule } from "@/types/validation";

const t = i18n.global.t;

export function useCheckCall() {
  function createCheckCallStateDisableRules(route: MaybeRefOrGetter<TripMonitorRoute>): DisableRule[] {
    const { nextRouteStop, status, redispatch } = toValue(route);

    const checkedInStatuses: RouteStopStatus[] = [
      RouteStopStatus.waitingForLoading,
      RouteStopStatus.waitingForGoodToGo,
    ];

    return [
      {
        disabled: !nextRouteStop,
        reasons: [t("trip.check-call-no-next-stop")],
      },
      {
        disabled: status === RouteStatus.cancelled,
        reasons: [t("trip.check-call-route-cancelled")],
      },
      {
        disabled: status === RouteStatus.planned,
        reasons: [t("trip.check-call-planned-route")],
      },
      {
        disabled: redispatch === RouteRedispatchState.needed,
        reasons: [t("trip.check-call-redispatch-needed")],
      },
      {
        disabled: nextRouteStop ? checkedInStatuses.includes(nextRouteStop.status) : false,
        reasons: [t("trip.check-call-driver-checked-in")],
      },
    ];
  }

  function createCheckCallDisableRules(route: MaybeRefOrGetter<TripMonitorRoute>): DisableRule[] {
    const { hasAccess } = useBaseView();

    return [
      {
        disabled: !hasAccess(DispatcherPermissionValue.CheckCallsEdit),
        reasons: [t("permission.create-check-call")],
      },
      ...createCheckCallStateDisableRules(route),
    ];
  }

  function isCheckCallDisabled(route: MaybeRefOrGetter<TripMonitorRoute>): boolean {
    return createCheckCallStateDisableRules(route).some((rule) => rule.disabled);
  }

  return { createCheckCallDisableRules, isCheckCallDisabled };
}
